import { useState } from 'react';
import { VehiculeConfig } from '../../../../shared/types/config';
import configService from '../../../../shared/services/configService';

interface UseVehiculeConfigParams {
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
  loadAllData: () => Promise<void>;
}

const emptyVehiculeForm: Omit<VehiculeConfig, 'id'> = {
  nom: '',
  niveaux: 1,
  prix: 0,
  specialiste: false,
  id_competences_armement: [],
  id_grades_priorite: [],
  id_grades_armement: [],
  kilometrage: 0,
  type: '',
  fonction_depart: '',
  capacite_personnels_min: 0,
  capacite_personnels_max: 0,
  capable_remorque: false,
  capable_lot: false,
  capable_cellule: false,
  capacite_eau: 0,
  capacite_emulseur: 0,
  id_service: 0,
  primary_armament_function_id: null,
  secondary_armament_function_ids: [],
  id_fonctions_armement: [],
  capacite_victimes_ur: 0,
  capacite_victimes_ua: 0,
  capacite_victimes_impliquees: 0,
  capacite_hostiles: 0,
  capacite_vehicules_accidentes: 0,
  capacite_animaux: 0,
  capable_escorte_medicale: false,
  capable_escorte_fdo: false,
};

export function useVehiculeConfig({
  setError,
  setSuccess,
  loadAllData,
}: UseVehiculeConfigParams) {
  const [vehiculeConfigs, setVehiculeConfigs] = useState<VehiculeConfig[]>([]);
  const [searchVehicule, setSearchVehicule] = useState('');
  const [showVehiculeModal, setShowVehiculeModal] = useState(false);
  const [editingVehicule, setEditingVehicule] = useState<VehiculeConfig | null>(null);
  const [vehiculeForm, setVehiculeForm] = useState<Omit<VehiculeConfig, 'id'>>(emptyVehiculeForm);

  const handleAddVehicule = () => {
    setEditingVehicule(null);
    setVehiculeForm({ ...emptyVehiculeForm });
    setShowVehiculeModal(true);
  };

  const handleEditVehicule = (vehicule: VehiculeConfig) => {
    setEditingVehicule(vehicule);
    const serviceIdRaw =
      vehicule.id_service ?? (vehicule as VehiculeConfig & { service_id?: number }).service_id;
    const idServiceNum = parseInt(String(serviceIdRaw ?? 0), 10);
    const id_service =
      Number.isInteger(idServiceNum) && idServiceNum >= 0 ? idServiceNum : 0;
    setVehiculeForm({
      nom: vehicule.nom,
      niveaux: vehicule.niveaux,
      prix: vehicule.prix,
      specialiste: vehicule.specialiste,
      kilometrage: vehicule.kilometrage,
      type: vehicule.type,
      fonction_depart: vehicule.fonction_depart,
      capacite_personnels_min: vehicule.capacite_personnels_min,
      capacite_personnels_max: vehicule.capacite_personnels_max,
      capable_remorque: vehicule.capable_remorque,
      capable_lot: vehicule.capable_lot,
      capable_cellule: vehicule.capable_cellule,
      capacite_eau: vehicule.capacite_eau,
      capacite_emulseur: vehicule.capacite_emulseur,
      id_service,
      primary_armament_function_id: vehicule.primary_armament_function_id ?? null,
      secondary_armament_function_ids:
        vehicule.secondary_armament_function_ids?.length
          ? [...vehicule.secondary_armament_function_ids]
          : vehicule.id_fonctions_armement?.length
            ? [...vehicule.id_fonctions_armement]
            : [],
      id_competences_armement: (() => {
        let ids = vehicule.id_competences_armement?.length
          ? [...vehicule.id_competences_armement]
          : [];
        const legacy =
          vehicule.armement_competence_id ?? vehicule.id_categorie_specialite;
        const legacyNum = legacy != null ? parseInt(String(legacy), 10) : NaN;
        if (ids.length === 0 && Number.isInteger(legacyNum) && legacyNum > 0) {
          ids = [legacyNum];
        }
        return ids;
      })(),
      id_grades_priorite: (vehicule.id_grades_priorite?.length ? vehicule.id_grades_priorite :
        vehicule.id_grades_armement?.length ? vehicule.id_grades_armement : []) as number[],
      id_grades_armement: (vehicule.id_grades_priorite?.length ? vehicule.id_grades_priorite :
        vehicule.id_grades_armement?.length ? vehicule.id_grades_armement : []) as number[],
      id_fonctions_armement:
        vehicule.secondary_armament_function_ids?.length
          ? [...vehicule.secondary_armament_function_ids]
          : vehicule.id_fonctions_armement?.length
            ? [...vehicule.id_fonctions_armement]
            : [],
      capacite_victimes_ur: vehicule.capacite_victimes_ur ?? 0,
      capacite_victimes_ua: vehicule.capacite_victimes_ua ?? 0,
      capacite_victimes_impliquees: vehicule.capacite_victimes_impliquees ?? 0,
      capacite_hostiles: vehicule.capacite_hostiles ?? 0,
      capacite_vehicules_accidentes: vehicule.capacite_vehicules_accidentes ?? 0,
      capacite_animaux: vehicule.capacite_animaux ?? 0,
      capable_escorte_medicale: Boolean(vehicule.capable_escorte_medicale),
      capable_escorte_fdo: Boolean(vehicule.capable_escorte_fdo),
    });
    setShowVehiculeModal(true);
  };

  const handleSaveVehicule = async () => {
    try {
      setError(null);
      const prio = vehiculeForm.id_grades_priorite?.length
        ? vehiculeForm.id_grades_priorite
        : vehiculeForm.id_grades_armement ?? [];
      const vehiculePayload: Omit<VehiculeConfig, 'id'> = {
        ...vehiculeForm,
        id_grades_priorite: prio,
        id_grades_armement: prio,
        secondary_armament_function_ids: Array.from(
          new Set((vehiculeForm.secondary_armament_function_ids || []).filter((id) => id !== vehiculeForm.primary_armament_function_id))
        ),
        id_fonctions_armement: Array.from(
          new Set((vehiculeForm.secondary_armament_function_ids || []).filter((id) => id !== vehiculeForm.primary_armament_function_id))
        ),
      };
      if (!vehiculePayload.id_service || vehiculePayload.id_service < 1) {
        setError('Sélectionnez un service pour ce véhicule.');
        return;
      }
      if (!vehiculePayload.primary_armament_function_id || vehiculePayload.primary_armament_function_id < 1) {
        setError('Sélectionnez une fonction d’armement principale.');
        return;
      }
      let result;
      if (editingVehicule?.id) {
        result = await configService.updateVehiculeConfig(editingVehicule.id, vehiculePayload);
      } else {
        result = await configService.createVehiculeConfig(vehiculePayload);
      }
      if (result.success) {
        setSuccess(editingVehicule ? 'Véhicule modifié avec succès' : 'Véhicule ajouté avec succès');
        setShowVehiculeModal(false);
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteVehicule = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;
    try {
      setError(null);
      const result = await configService.deleteVehiculeConfig(id);
      if (result.success) {
        setSuccess('Véhicule supprimé avec succès');
        await loadAllData();
      } else {
        setError(result.error || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  return {
    vehiculeConfigs,
    setVehiculeConfigs,
    searchVehicule,
    setSearchVehicule,
    showVehiculeModal,
    setShowVehiculeModal,
    editingVehicule,
    vehiculeForm,
    setVehiculeForm,
    handleAddVehicule,
    handleEditVehicule,
    handleSaveVehicule,
    handleDeleteVehicule,
  };
}
